import { createContext, useContext, type ReactNode } from 'react'
import { Chip } from '../primitives/Chip'
import { Loading } from '../patterns/Loading'
import { PageHeader } from '../patterns/PageHeader'
import { QueryState, type QueryLike } from '../patterns/QueryState'
import s from './Screen.module.css'

/** What <Screen> reads off a response, when it's there. Every data response carries `example`. */
export interface ScreenData {
  example?: boolean
  /** ISO time the numbers were worked out: shown as "As of 2:14 pm". */
  asOf?: string
  /** IANA timezone to show `asOf` in (the store's), e.g. "America/New_York". */
  timezone?: string
}

/** The registry's title and description for the open screen. The app provides it, so screens don't repeat them. */
export const ScreenMeta = createContext<{ title: string; description: string } | null>(null)

/**
 * One screen's frame: the header (title, description, Example chip, how fresh), then the states, then the content.
 * The screen only says what to draw once data exists:
 *
 *   const query = useApi<TodayResponse>('/api/today')
 *   return <Screen query={query}>{(data) => <Stack>…</Stack>}</Screen>
 *
 * - First load: placeholders in the shape of a page (`loading` picks another shape).
 * - Reload: the old numbers stay, with an "Updating" chip.
 * - Error: "This didn't load", the reason in plain words, Try again. If old numbers exist they stay, with a chip.
 * - Title and description come from the screen registry; pass them only to override (a description that needs data).
 */
export function Screen<T extends ScreenData>(props: {
  query: QueryLike<T>
  children: (data: T) => ReactNode
  title?: string
  description?: string | ((data: T) => string)
  actions?: ReactNode
  loading?: 'page' | 'block' | 'list'
}) {
  const meta = useContext(ScreenMeta)
  const { data, error, loading } = props.query
  const title = props.title ?? meta?.title ?? ''
  const description =
    typeof props.description === 'function'
      ? data
        ? props.description(data)
        : meta?.description
      : (props.description ?? meta?.description)
  const chips = (
    <>
      {loading && data !== undefined && <Chip>Updating</Chip>}
      {error && data !== undefined && (
        <Chip tone="warn" title={error.message}>
          Couldn't update
        </Chip>
      )}
    </>
  )
  return (
    <div className={s.screen}>
      <PageHeader
        title={title}
        description={description}
        example={data?.example}
        meta={data?.asOf ? asOfText(data.asOf, data.timezone) : undefined}
        chips={chips}
        actions={props.actions}
      />
      <QueryState query={props.query} loading={<Loading shape={props.loading ?? 'page'} />}>
        {props.children}
      </QueryState>
    </div>
  )
}

/** "As of 2:14 pm", in the given timezone (the store's) when known. */
export function asOfText(iso: string, timeZone?: string): string {
  const time = new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  })
  return `As of ${time.replace(/\s?AM$/, ' am').replace(/\s?PM$/, ' pm')}`
}
